import fs from "fs";
import path from "path";
import { CompatibilityIssue } from "../analyze/models.js";
import { AnalysisBaseline } from "../models/baseline.js";

const BASELINE_DIR_NEW = ".kyro";
const BASELINE_DIR_LEGACY = ".rn-orchestrator";
const BASELINE_FILE = "baseline.json";

/**
 * Derives a stable, deterministic ID for a CompatibilityIssue.
 *
 * Format: `<severity>:<packageName>:<normalized-message>`
 *
 * The message is normalized (lowercased, whitespace collapsed) so that
 * minor formatting changes don't invalidate the baseline.
 *
 * No timestamps or random components are included.
 */
export function deriveIssueId(issue: CompatibilityIssue): string {
  const normalizedMessage = issue.message.toLowerCase().replace(/\s+/g, " ").trim();
  return `${issue.severity}:${issue.packageName}:${normalizedMessage}`;
}

/**
 * Returns a new baseline containing the stable IDs of all provided issues.
 */
export function createBaseline(issues: CompatibilityIssue[]): AnalysisBaseline {
  const issueIds = issues.map(deriveIssueId).sort();
  return {
    generatedAt: new Date().toISOString(),
    issueIds,
  };
}

/**
 * Writes a baseline to `.kyro/baseline.json`.
 * Creates the directory if it does not exist.
 */
export function writeBaseline(projectRoot: string, baseline: AnalysisBaseline): void {
  const dir = path.join(projectRoot, BASELINE_DIR_NEW);
  const filePath = path.join(dir, BASELINE_FILE);

  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }

  fs.writeFileSync(filePath, JSON.stringify(baseline, null, 2), "utf-8");
}

/**
 * Loads the baseline from `.kyro/baseline.json`.
 * Falls back to `.rn-orchestrator/baseline.json` with warning if needed.
 * Returns null if the file does not exist or is unreadable.
 */
export function loadBaseline(projectRoot: string): AnalysisBaseline | null {
  const newPath = path.join(projectRoot, BASELINE_DIR_NEW, BASELINE_FILE);
  const legacyPath = path.join(projectRoot, BASELINE_DIR_LEGACY, BASELINE_FILE);

  let filePath = newPath;
  let hasLegacy = false;

  if (fs.existsSync(newPath)) {
    filePath = newPath;
  } else if (fs.existsSync(legacyPath)) {
    filePath = legacyPath;
    hasLegacy = true;
  } else {
    return null;
  }

  if (hasLegacy) {
    console.warn("Legacy rn-orchestrator configuration detected. Please migrate to .kyro/config.json");
  }

  try {
    const raw = fs.readFileSync(filePath, "utf-8");
    const parsed = JSON.parse(raw) as AnalysisBaseline;
    if (!Array.isArray(parsed.issueIds)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Filters a list of issues, removing any whose ID appears in the baseline.
 * Issues not in the baseline are considered newly introduced.
 */
export function filterBaselineIssues(
  issues: CompatibilityIssue[],
  baseline: AnalysisBaseline
): CompatibilityIssue[] {
  const knownIds = new Set(baseline.issueIds);
  return issues.filter((issue) => !knownIds.has(deriveIssueId(issue)));
}
